import React from 'react'

import {GoogleMap, LoadScript} from '@react-google-maps/api'
// @ts-ignore
import _ from 'lodash'

import MapMarker from "./marker/map-marker"
import QuestsCollection from "../models/quests/quests-collection"
import PointsCollection from "../models/points/points-collection"
// @ts-ignore
import LoadingScreen from "./loading"
import {COLOR} from "../constants/color"
import Preferences from "./preferences/prefernces"
import InfoPanel from "./info/info-panel"
import Refresh from "./refresh/refresh"
import {getCoordinates, isModelVisible} from "../utils/utils"
import Add from "./add/add"
import Point from "../models/points/point"
import Config from "../config/config"
import ErrorMessage from "./error-message/error-message"
import {computeTriangulatedPoints} from "../utils/triangulation"
import {groupClosePoints} from "../utils/grouping"
import GroupMarker from "./marker/group-marker"
import GroupPanel from "./group/group-panel"

const containerStyle = {
    width: '100%',
    height: '100%'
}

type Props = {}

type State = {
    items           : any
    isLoaded        : boolean
    error?          : string | null
    showError?      : boolean
    loadingProgress : number
    loadingPhase    : string
}

const ERROR_DELAY = 1800 // ms
const GROUP_DISTANCE = 30 // px, about one marker icon

// const debug = "Map"
const debug = false

export default class Map extends React.Component<Props, State> {

    private isCenteredToUserLocation    : boolean
    private customPoints?               : PointsCollection
    private triangulatedPoints          : Point[] = []
    private map                         : any
    private accuracyCircle              : any
    private marker                      : any
    private zoom                        : number = 0
    private showErrorTimer: any = null

    constructor(props: Props) {
        super(props)

        this.isCenteredToUserLocation   = false

        this.state                      = {
            items           : {},
            isLoaded        : false,
            error           : null,
            showError       : false,
            loadingProgress : 0,
            loadingPhase    : 'Initializing...',
        }
    }

    async componentDidMount() {
        window.bus.subscribe("reload-data", this, this.reloadData)
        window.bus.subscribe("add-points", this, this.addPoints)
        window.bus.subscribe("update-point", this, this.updatePoint)
        window.bus.subscribe("delete-point", this, this.deletePoint)
        window.bus.subscribe("clear-points", this, this.clearPoints)
        window.bus.subscribe("preferences:filters-changed", this, this.onFiltersChanged)

        this.setState({
            loadingProgress : 5,
            loadingPhase    : 'Loading config...'
        })
        await Config.load()
        this.setState({
            loadingProgress : 15,
            loadingPhase    : 'Config loaded'
        })
        this.parseParams()
        this.loadData()
    }

    componentWillUnmount() {
        window.bus.unsubscribe(this)
        if (this.showErrorTimer) {
            clearTimeout(this.showErrorTimer)
        }
    }

    parseParams = () => {
        const params = decodeURI(window.location.pathname).split('/')
        debug && console.log(debug, "parseParams", params)

        _.map(params, (p: any) => {
            const coordinates = getCoordinates(p)
            if (coordinates) Config.get().center = coordinates
        })
    }

    reloadData = () => {
        debug && console.log(debug, "reloadData")
        if (this.showErrorTimer) clearTimeout(this.showErrorTimer)
        this.setState({
            error           : null,
            showError       : false,
            loadingProgress : 0,
            loadingPhase    : 'Reloading...'
        })
        this.loadData(()=>{
            debug && console.log(debug, "reloadData completed")
            window.bus.triggerEvent("reload-completed")
        })
    }

    // Added skipLoadData to set state as loaded even if there was an error
    skipLoadData = () => {
        this.setState({
            isLoaded: true,
            showError: false,
        });
    }

    loadData = async (callback?: any) => {
        const config = Config.get()
        debug && console.log(debug, "loadData", config)

        this.customPoints = PointsCollection.initFromJSON(window.localSettings.customPoints)
        this.triangulatedPoints = computeTriangulatedPoints(this.customPoints?.models || [])

        try {
            this.setState({
                loadingProgress : 25,
                loadingPhase    : 'Fetching data...'
            })

            const [questsRes, pointsRes] = await Promise.all([
                fetch(config.questsUrl),
                fetch(config.pointsUrl)
            ])

            this.setState({
                loadingProgress : 55,
                loadingPhase    : 'Parsing data...'
            })

            const [questsData, pointsData] = await Promise.all([questsRes?.json() || {}, pointsRes?.json() || {}])

            debug && console.log(debug, "loadData questsData", questsData)
            debug && console.log(debug, "loadData pointsData", pointsData)

            await new Promise(r => setTimeout(r, 350))

            this.setState({
                loadingProgress : 80,
                loadingPhase    : 'Building map...'
            })

            await new Promise(r => setTimeout(r, 350))

            if (this.showErrorTimer) clearTimeout(this.showErrorTimer)

            this.setState({
                items       : {
                    quests      : QuestsCollection.initFromJSON(questsData),
                    points      : PointsCollection.initFromJSON(pointsData),
                },
                isLoaded        : true,
                error           : null,
                showError       : false,
                loadingProgress : 100,
                loadingPhase    : 'Ready!',
            }, callback)
        } catch (error) {
            console.error("Error loading data:", error)
            debug && console.log(debug, "loadData error", error)

            // Set a timer to show a user error message (recommend: 1-3s, here set to 1.8s)
            if (this.showErrorTimer) clearTimeout(this.showErrorTimer)
            this.showErrorTimer = setTimeout(() => {
                this.setState({
                    showError: true
                })
            }, ERROR_DELAY)

            this.setState({
                items       : {
                    quests      : [],
                    points      : [],
                },
                isLoaded    : false,
                error       : "Could not load map data."
            }, callback)
        }
    }

    addPoints = (arrayData: any[]) => {
        debug && console.log(debug, "addPoints", arrayData, this.customPoints)

        _.map(arrayData, (pointData: any) => {
            const newPoint = new Point(pointData)
            const point = this.customPoints?.getByCoordinates(newPoint.lat, newPoint.lng)

            if (point) {
                this.customPoints?.replace(point.id, newPoint)
            } else {
                this.customPoints?.add(new Point(pointData))
            }
        })

        window.localSettings.customPoints = this.customPoints?.toJSON()
        window.localSettings.save()
        this.triangulatedPoints = computeTriangulatedPoints(this.customPoints?.models || [])
        this.forceUpdate()
    }

    updatePoint = (id: ID, pointData: any) => {
        debug && console.log(debug, "updatePoint", id, pointData, this.customPoints)
        this.customPoints?.replace(id, new Point({ ...pointData, id }))

        window.localSettings.customPoints = this.customPoints?.toJSON()
        window.localSettings.save()
        this.triangulatedPoints = computeTriangulatedPoints(this.customPoints?.models || [])
        this.forceUpdate()
    }

    // accepts a single id or an array (calculated point deletes all of its observers)
    deletePoint = (id: ID | ID[]) => {
        debug && console.log(debug, "deletePoint", id, this.customPoints)
        _.forEach(_.castArray(id), (pointId: ID) => {
            this.customPoints?.remove(pointId)
            return
        })

        window.localSettings.customPoints = this.customPoints?.toJSON()
        window.localSettings.save()
        this.triangulatedPoints = computeTriangulatedPoints(this.customPoints?.models || [])
        this.forceUpdate()
    }

    clearPoints = () => {
        debug && console.log(debug, "clearPoints")

        this.customPoints?.reset()
        window.localSettings.customPoints = []
        window.localSettings.save()
        this.triangulatedPoints = []
        this.forceUpdate()
    }

    onClickMap = (ev: any) => {
        debug && console.log(debug, "onClickMap", ev)

        // If the event is a POI
        if (ev.placeId) {

            // Call event.stop() on the event to prevent the default info window from showing.
            ev.stop();
            debug && console.log(debug, "onClickMap", ev.placeId, ev.latLng)
        }

        window.bus.triggerEvent("info-panel:hide")
    }

    onFiltersChanged = () => {
        this.forceUpdate()
        return
    }

    // runs once map settles after pan/zoom — regrouping on every zoom_changed tick of an animated zoom is wasted work
    onIdle = () => {
        if (!this.map) {
            return
        }

        const zoom = this.map.getZoom()
        if (zoom === this.zoom) {
            return
        }

        this.zoom = zoom
        if (window.localSettings.groupClosePoints) {
            this.forceUpdate()
        }
        return
    }

    // groups of markers overlapping on screen at current zoom; single-item groups are left as plain markers
    getGroups = (allItems: any[]): any[][] => {
        if (!window.localSettings.groupClosePoints) {
            return []
        }

        const groups = groupClosePoints(_.filter(allItems, isModelVisible), this.zoom, GROUP_DISTANCE)
        return _.filter(groups, (group: any[]) => {
            return group.length > 1
        })
    }

    onLoad = (map: any) => {
        this.map = map

        debug && console.log(debug, "onLoad", this.map)

        this.onIdle()

        this.updateLocation()
    }

    updateLocation = () => {
        debug && console.log(debug, "updateLocation", navigator)

        navigator.geolocation.watchPosition(position=>{
            debug && console.log(debug, "updateLocation position", position)

            const userLocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
            }
            this.setCenter(userLocation)

            const errorRange = position.coords.accuracy

            if (this.accuracyCircle) {
                this.accuracyCircle.setCenter(userLocation)
                this.accuracyCircle.setRadius(errorRange)
            } else {
                this.accuracyCircle = new window.google.maps.Circle({
                    center          : userLocation,
                    fillColor       : COLOR.GOOGLE_BLUE_DARK,
                    fillOpacity     : 0.4,
                    radius          : errorRange,
                    strokeColor     : COLOR.GOOGLE_BLUE_LIGHT,
                    strokeOpacity   : 0.4,
                    strokeWeight    : 1,
                    zIndex          : 1,
                })
                this.accuracyCircle.setMap(this.map)
            }


            if (this.marker) {
                this.marker.position = userLocation
            } else {
                const container = document.createElement('div')
                container.style.cssText = 'position:relative;width:0;height:0'
                const dot = document.createElement('div')
                dot.style.cssText = [
                    'position:absolute',
                    'width:16px',
                    'height:16px',
                    `background:${COLOR.GOOGLE_BLUE}`,
                    `border:2px solid ${COLOR.WHITE}`,
                    'border-radius:50%',
                    'transform:translate(-50%,-50%)',
                ].join(';')
                container.appendChild(dot)

                this.marker = new (window as any).google.maps.marker.AdvancedMarkerElement({
                    position : userLocation,
                    content  : container,
                    title    : 'You are here!',
                    map      : this.map,
                })
            }

        }, (e)=>{
            debug && console.log(debug, "updateLocation error", e)
        })
    }

    setCenter(userLocation: any) {
        debug && console.log(debug, "setCenter", this.isCenteredToUserLocation)

        if (!this.isCenteredToUserLocation) {
            this.isCenteredToUserLocation = true

            const config = Config.get()
            config.center = userLocation
            this.forceUpdate()
        }
    }

    render() {
        debug && console.log(debug, "render", this.state.items, this.customPoints)

        // Show error UI after delay, if there was an error
        if (!this.state.isLoaded && this.state.error && this.state.showError) {
            // Replace onRetry with skipLoadData, label as "Skip" instead of "Retry"
            return (
                <ErrorMessage
                    message = {this.state.error}
                    onSkip  = {this.skipLoadData}
                />
            )
        }

        // Still loading (or before error delay)
        if (!this.state.isLoaded) {
            return (
                <LoadingScreen
                    progress    = {this.state.loadingProgress}
                    phase       = {this.state.loadingPhase}
                />
            )
        }

        const config = Config.get()
        const points    = this.state.items.points
        const quests    = this.state.items.quests
        const allItems  = _.concat(
            points.models || [],
            quests.models || [],
            this.customPoints?.models || [],
            this.triangulatedPoints
        )
        const groups    = this.getGroups(allItems)
        const grouped   = new Set(_.flatten(groups))

        return (
            <React.Fragment>
                <LoadScript
                    googleMapsApiKey    = {config.googleMapsApiKey}
                    libraries           = {["marker"] as any}
                    loadingElement      = {<LoadingScreen phase="Loading map engine..."/>}
                >
                    <GoogleMap
                        mapContainerStyle    = {containerStyle}
                        center               = {config.center}
                        zoom                 = {config.zoom}
                        options              = {{
                            fullscreenControl: false,
                            mapId: config.mapId,
                        }}
                        onClick              = {this.onClickMap}
                        onLoad               = {this.onLoad}
                        onIdle               = {this.onIdle}
                    >
                        {_.map(allItems, (data: any) => {
                            return (
                                <MapMarker
                                    key     = {(data.isCustom ? 'custom' : data.type) + data.id}
                                    data    = {data}
                                    grouped = {grouped.has(data)}
                                />
                            )
                        })}

                        {_.map(groups, (group: any[], key: number) => {
                            return (
                                <GroupMarker
                                    key     = {key}
                                    items   = {group}
                                />
                            )
                        })}
                    </GoogleMap>
                </LoadScript>
                <InfoPanel items={this.state.items}/>
                <GroupPanel/>
                <Add/>
                <Refresh/>
                <Preferences items={this.state.items}/>
            </React.Fragment>
        )
    }
}